// CABLE SIGNING, IN THE SIGNER'S READING ORDER (UI rebuild batch 4, custody hand-off; Astra's `cable-sign`).
//
// THE LOGIC IS screens/custody/CableSign.tsx's, COPIED: the card read through the mint gate (only the latest opening's
// answer lands), checkCard (sign-card.ts over cosign/src/verify.ts — the signing page's own functions, in the page's own
// order), the typing gate (the last six, shown nowhere until typed; the confirmation holds for this card and this
// address only — confirmationHolds), the re-read and re-check at the tap, signOnLedger with the bytes that were checked,
// the signature posted, the destination remembered only after the server took it, and every refusal sentence
// (cardRefusal, signatureRefusal, signedSentence, refusalIsDone). Nothing is decided here that the sheet does not decide.
//
// What changed is the ORDER ON SCREEN, to match the signing page's restyle (docs/ui-rebuild/handoff/SIGNING.md):
// refusals first → amount, then the named recipient and the network → the typing gate → the Nano comparison line →
// Sign. The source address, fee limit and transaction id fold under "Verification details".
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { custody as api, type SigningCard } from '../../../lib/api';
import { ApiError } from '../../../lib/errors';
import { fmtTime } from '../../../lib/format';
import { groupDigits, KIND_WORD } from '../../../lib/custody-format';
import { webHid, signOnLedger, buildNetwork, loadConfirmed, confirmDestination, CONFIRM_TAIL, type LedgerStep, type EntryKind } from '../../../lib/ledger-cable';
import { checkCard, anchorSentence, confirmInstruction, type CardCheck } from '../../../screens/custody/sign-card';
import { signatureRefusal, signedSentence, refusalIsDone } from '../../../screens/custody/refusals';
import { makeMintGate, confirmationHolds, oneAtATime } from '../../../lib/custody-screen';
import { DetailSheet, StateLine, Pending, Fact } from '../kit';
import { MoreDetails } from '../../MoreDetails';

type Phase =
  | { at: 'loading' }
  | { at: 'refused'; text: string; done?: boolean }
  | { at: 'checked'; check: CardCheck }
  | { at: 'signing'; check: CardCheck; step: LedgerStep | null; text: string }
  | { at: 'signed'; check: CardCheck; text: string }
  | { at: 'failed'; check: CardCheck; text: string; remedy: string | null; askedDevice: boolean };

const textOf = (err: unknown): string => String((err as { message?: unknown } | null)?.message ?? err);
const remedyOf = (err: unknown): string | null => {
  const remedy = (err as { remedy?: unknown } | null)?.remedy;
  return typeof remedy === 'string' && remedy ? remedy : null;
};

/** CableSign.tsx cardRefusal, word for word. */
function cardRefusal(err: unknown): { text: string; done: boolean } | null {
  if (!(err instanceof ApiError)) return null;
  const reason = typeof err.body.reason === 'string' ? err.body.reason : null;
  switch (reason) {
    case 'approval_signed':
      return { done: true, text: 'Already signed. The signature is recorded and the desk sends it to the network — nothing more to do here.' };
    case 'approval_expired':
      return { done: false, text: 'The signing window for this transaction has closed, and nothing was sent. A fresh one appears in the list while an approval window is open.' };
    case 'approval_superseded':
      return { done: false, text: 'This transaction was replaced by a newer build. Open the newer one from the list.' };
    default:
      return null;
  }
}

export function CableSignV2({ txid, onClose }: { txid: string; onClose: () => void }) {
  const qc = useQueryClient();
  const network = useMemo(() => buildNetwork(), []);
  const gate = useMemo(() => makeMintGate(), []);
  const [phase, setPhase] = useState<Phase>({ at: 'loading' });
  const [typed, setTyped] = useState('');
  const [typedFor, setTypedFor] = useState<{ txid: string; address: string; partyName: string; kind: EntryKind; layer?: string | null } | null>(null);

  const load = useCallback(async () => {
    setPhase({ at: 'loading' });
    setTyped('');
    setTypedFor(null);
    if (!network) {
      setPhase({ at: 'refused', text: 'This build of the app names no TRON network, so it cannot sign. Report this — the app was set up wrongly.' });
      return;
    }
    try {
      const read = await gate.open(() => api.card(txid));
      if (read.stale) return;
      setPhase({ at: 'checked', check: checkCard(read.value, { txid, network, now: Date.now(), confirmed: loadConfirmed(network) }) });
    } catch (err) {
      const refusal = cardRefusal(err);
      if (refusal) { setPhase({ at: 'refused', ...refusal }); return; }
      setPhase({ at: 'refused', text: `Couldn't read the card — ${textOf(err)}` });
    }
  }, [txid, network, gate]);

  useEffect(() => {
    void load();
    return () => gate.close();
  }, [load, gate]);

  const check = 'check' in phase ? phase.check : null;
  const facts = check?.facts ?? null;
  const tail = facts ? facts.to.slice(-CONFIRM_TAIL) : '';
  const endingTyped = confirmationHolds(typedFor, { txid, to: facts?.to ?? null });
  const armed = !!check && (check.ready || (check.mustConfirm && endingTyped));

  function onType(value: string) {
    setTyped(value);
    if (!check || !facts || typedFor !== null || !check.mustConfirm) return;
    if (value.trim() !== tail) return;
    if (!check.anchor || !('kind' in check.anchor) || !('partyName' in check.anchor)) return;
    setTypedFor({ txid, address: facts.to, partyName: check.anchor.partyName, kind: check.anchor.kind, layer: check.anchor.kind === 'desk' ? check.destinationLayer : null });
  }

  // One ceremony at a time, guarded SYNCHRONOUSLY before the first await (Screens and Astra, 27 Oct 2026, #4): the card
  // is read again before the device is asked, and the button used to disable only once the phase said 'signing' — two
  // taps during a slow read started two ceremonies.
  const [once] = useState(oneAtATime);

  async function sign() {
    await once(signOnce);
  }

  async function signOnce() {
    if (!check || !facts || !armed || !network || check.signer === null) return;
    const live = gate.claim();
    const show = (p: Phase) => { if (live()) setPhase(p); };
    const cable = webHid();
    if (!cable) {
      show({ at: 'failed', check, text: 'This browser cannot reach a Ledger over a cable.', remedy: 'Use Brave or Edge on the computer the Ledger is plugged into, or sign on a phone from the link.', askedDevice: false });
      return;
    }
    let fresh: SigningCard;
    try {
      fresh = await api.card(txid);
    } catch (err) {
      const refusal = cardRefusal(err);
      if (refusal) {
        show({ at: 'refused', ...refusal });
        void qc.invalidateQueries({ queryKey: ['custody', 'approvals'] });
        return;
      }
      show({ at: 'failed', check, text: `Couldn't read the card — ${textOf(err)}`, remedy: 'Nothing was signed. Close this and open the card again.', askedDevice: false });
      return;
    }
    const again = checkCard(fresh, { txid, network, now: Date.now(), confirmed: loadConfirmed(network) });
    const confirmed = confirmationHolds(typedFor, { txid, to: again.facts?.to ?? null });
    if (again.problems.length || again.signer === null || (!again.ready && !(again.mustConfirm && confirmed))) {
      show({ at: 'failed', check: again, text: again.problems[0] ?? 'The card no longer passes its checks.', remedy: 'Nothing was signed. Close this and open the card again.', askedDevice: false });
      return;
    }
    // Closed or replaced while the card was re-read: the device is never asked for this tap (Astra, max, pre-live,
    // 29 Oct 2026 — as screens/custody/CableSign.tsx).
    if (!live()) return;
    let askedDevice = false;
    let deviceSigned = false;
    show({ at: 'signing', check: again, step: null, text: 'Starting…' });
    try {
      const { signature } = await signOnLedger({
        hid: cable, signer: again.signer, rawDataHex: fresh.rawDataHex, network,
        derivation: again.derivation,
        account: fresh.source?.account ?? undefined,
        prove: again.prove,
        onStep: (step, text) => {
          if (step === 'sign') askedDevice = true;
          show({ at: 'signing', check: again, step, text });
        },
      });
      deviceSigned = true;
      // Closed while the device was signing: the signature is dropped, never recorded.
      if (!live()) return;
      show({ at: 'signing', check, step: 'sign', text: 'Recording the signature…' });
      const answer = await api.submitSignature(txid, signature);
      const remember = confirmed ? typedFor : null;
      const remembered = remember === null || confirmDestination(network, remember);
      const notKept = remember === null || remembered ? ''
        : ' This browser would not save the destination, so it will ask you to confirm it again next time.';
      show({ at: 'signed', check, text: signedSentence(answer) + notKept });
      void qc.invalidateQueries({ queryKey: ['custody', 'approvals'] });
      void qc.invalidateQueries({ queryKey: ['custody', 'summary'] });
    } catch (err) {
      if (err instanceof ApiError) {
        const reason = typeof err.body.reason === 'string' ? err.body.reason : null;
        show({
          at: 'failed', check, askedDevice,
          text: signatureRefusal(err.status, reason),
          remedy: refusalIsDone(reason) ? null
            : reason !== null ? 'Your device did sign, and the desk did not record that signature: nothing will be sent with it.'
            : 'Your device did sign. Whether the desk recorded it is not known here — check the list before you try again.',
        });
        return;
      }
      show({
        at: 'failed', check, askedDevice,
        text: textOf(err),
        remedy: remedyOf(err) ?? (deviceSigned ? 'Your device did sign. Whether the desk recorded it is not known here — check the list before you try again.' : null),
      });
    }
  }

  const canConfirm = !!check?.mustConfirm;
  const banner = check?.anchor ? anchorSentence(check.anchor, canConfirm && !endingTyped) : null;
  const recipient = check?.anchor && 'partyName' in check.anchor ? check.anchor.partyName : 'no payable entry';
  const signWord = phase.at === 'signing' ? 'On the Ledger…'
    : phase.at === 'failed' ? 'Try again'
    : check?.problems.length ? 'Refusing to sign'
    : canConfirm && !endingTyped ? 'Confirm the address to continue'
    : check && !check.ready && !endingTyped ? 'Refusing to sign'
    : 'Sign on Ledger';

  return (
    <DetailSheet
      title="Sign on Ledger · cable"
      onClose={onClose}
      footer={check && facts ? (
        phase.at === 'signed'
          ? <button type="button" className="btn-primary w-full" onClick={onClose}>Done</button>
          : <button type="button" className="btn-primary w-full" disabled={!armed || phase.at === 'signing'} onClick={() => void sign()}>{signWord}</button>
      ) : undefined}
    >
      <div className="space-y-4">
        {phase.at === 'loading' && <Pending what="Reading…" />}
        {/* words:ok — the refusal is the signing page's own sentence (cardRefusal), shown whole */}
        {phase.at === 'refused' && <StateLine tone={phase.done ? 'good' : 'bad'}>{phase.text}</StateLine>}

        {/* 1 · REFUSALS FIRST — each the card check's own sentence (sign-card.ts), shown whole. */}
        {/* words:ok — sign-card.ts problem sentences */}
        {check && check.problems.map((p) => <StateLine key={p} tone="bad">{p}</StateLine>)}
        {/* words:ok — the failure is the signing page's own sentence; its remedy sits behind Details */}
        {check && !facts && phase.at === 'failed' && (
          <StateLine tone="bad" more={phase.remedy ?? (!phase.askedDevice ? 'This sheet has not asked your device to sign anything.' : undefined)}>{phase.text}</StateLine>
        )}

        {check && facts && (
          <>
            {/* 2 · AMOUNT, then the named recipient and the network — decoded from the signed bytes. */}
            <section className="rounded-[10px] border border-stroke">
              <div className="border-b border-stroke p-4">
                <div className="text-label font-medium uppercase tracking-wider text-tg-hint">{KIND_WORD[facts.kind] ?? 'Card'}{facts.network === 'nile' ? ' · Nile' : ''}</div>
                <div className="mt-1 font-mono tabular text-figure font-semibold">{groupDigits(facts.amount)} <span className="text-meta font-normal text-tg-hint">USDT</span></div>
                <div className="mt-1 text-meta text-tg-hint">{facts.network === 'nile' ? 'TRON Nile' : 'TRON'} · from {facts.fromName}</div>
              </div>
              <div className="px-4">
                <Fact label="Recipient"><span className="font-medium">{recipient}</span></Fact>
                <Fact label="Address"><span className="break-all font-mono text-meta">{endingTyped ? facts.to : facts.toShown}</span></Fact>
                {facts.closesAt !== null && <Fact label="Sign before">{fmtTime(facts.closesAt)} · {Math.max(0, Math.floor((facts.closesAt - Date.now()) / 60_000))} min left</Fact>}
              </div>
            </section>

            {/* words:ok — the anchor's sentence (sign-card.ts anchorSentence), shown whole */}
            {banner && !endingTyped && <StateLine tone={banner.tone === 'ok' ? 'good' : banner.tone === 'notice' ? 'warn' : 'bad'}>{banner.text}</StateLine>}
            {endingTyped && typedFor && (
              <StateLine tone="good" more="An address can be made to end the same way: compare the whole address with your record, then again on the Ledger's screen.">
                The ending matches — compare the whole address.
              </StateLine>
            )}

            {/* 3 · THE TYPING GATE — the last six, shown nowhere until typed. */}
            {canConfirm && check.anchor && 'partyName' in check.anchor && 'kind' in check.anchor && (
              <section className={`rounded-[10px] border p-4 ${endingTyped ? 'border-stroke' : 'border-neg/50'}`}>
                <h3 className="text-title font-semibold">Confirm this address</h3>
                <StateLine className="mt-1" more={confirmInstruction(check.anchor, CONFIRM_TAIL)}>Type the last six characters from your own record.</StateLine>
                <input
                  className="input mt-3 w-full text-center font-mono tracking-[0.12em]"
                  value={typed}
                  disabled={endingTyped}
                  maxLength={CONFIRM_TAIL}
                  placeholder={'•'.repeat(CONFIRM_TAIL)}
                  aria-label={`Last ${CONFIRM_TAIL} characters of the address`}
                  autoComplete="off" autoCapitalize="off" spellCheck={false}
                  onChange={(e) => onType(e.target.value)}
                />
                {!endingTyped && <p className="mt-2 text-meta text-tg-hint">Sign opens when this matches.</p>}
              </section>
            )}

            {/* 4 · THE NANO — the one display a compromised server cannot reach. */}
            <div className="rounded-lg border border-stroke bg-panel-2 px-4 py-3">
              <StateLine more={facts.network === 'nile'
                ? <>On the test network the Ledger shows a <b>Custom Contract</b> call, not the amount: check <b>Contract</b> is the test USDT and <b>From</b> (after “P{facts.permission} -”) is the source address. Anything different: reject on the device.</>
                : <>On the Ledger, compare <b>Amount</b>, <b>To</b> (every character) and <b>From</b> (after “P{facts.permission} -”) with this sheet. Anything different: reject on the device.</>}>
                Compare recipient and amount on the Nano.
              </StateLine>
            </div>

            {/* words:ok — the device's own step, as ledger-cable words it */}
            {phase.at === 'signing' && <StateLine>{phase.text}</StateLine>}
            {/* words:ok — the signed sentence is the signing page's own (refusals.ts signedSentence) */}
            {phase.at === 'signed' && <StateLine tone="good">{phase.text}</StateLine>}
            {/* words:ok — the failure is the signing page's own sentence; its remedy sits behind Details */}
            {phase.at === 'failed' && (
              <StateLine tone="bad" more={phase.remedy ?? (!phase.askedDevice ? 'This sheet has not asked your device to sign anything.' : undefined)}>{phase.text}</StateLine>
            )}

            <MoreDetails label="Verification details">
              <div className="text-meta">
                <Fact label="From address"><span className="break-all font-mono">{facts.from}</span></Fact>
                <Fact label="Fee limit"><span className="font-mono">{groupDigits(facts.feeLimit)} TRX</span></Fact>
                <Fact label="Permission">{facts.permission}</Fact>
                <Fact label="Transaction"><span className="break-all font-mono">{facts.txIdGroups.join(' ')}</span></Fact>
                <p className="py-3 text-tg-hint">Decoded here from the signed bytes. Ledger Live closed, device unlocked, TRON app open.</p>
              </div>
            </MoreDetails>
          </>
        )}
      </div>
    </DetailSheet>
  );
}
